// DEPRECATED: 

<script lang="ts">
// Predefined variants for different notification types
const VARIANTS = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success'
} as const

const POSITIONS = {
  TOP: 'top',
  BOTTOM: 'bottom'
} as const

// Centralized theme configuration for strict consistency
const theme = {
  [VARIANTS.ERROR]: {
    container: 'bg-surface border border-danger text-on-surface',
    icon: 'text-danger'
  },
  [VARIANTS.WARNING]: {
    container: 'bg-surface border border-warning text-on-surface',
    icon: 'text-warning'
  },
  [VARIANTS.INFO]: {
    container: 'bg-surface border border-primary text-on-surface',
    icon: 'text-primary'
  },
  [VARIANTS.SUCCESS]: {
    container: 'bg-surface border border-success text-on-surface',
    icon: 'text-success'
  }
}

const positionClasses = {
  [POSITIONS.TOP]: 'top-4 left-1/2 transform -translate-x-1/2',
  [POSITIONS.BOTTOM]: 'bottom-4 left-1/2 transform -translate-x-1/2'
}
</script>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import BaseButton from './BaseButton.vue'

type Variant = (typeof VARIANTS)[keyof typeof VARIANTS]
type Position = (typeof POSITIONS)[keyof typeof POSITIONS]

const props = withDefaults(defineProps<{
  message: string
  variant?: Variant
  position?: Position
  dismissible?: boolean
  autoDismiss?: boolean
  dismissTimeout?: number
}>(), {
  variant: VARIANTS.ERROR,
  position: POSITIONS.TOP,
  dismissible: true,
  autoDismiss: false,
  dismissTimeout: 5000,
})

const emit = defineEmits<{
  (event: 'dismiss'): void
}>()

// Component state
const isVisible = ref<boolean>(true)
const dismissTimer = ref<ReturnType<typeof setTimeout> | null>(null)

// Computed classes based on variant and position
const containerClasses = computed(() => {
  const baseClasses = [
    'fixed z-50 max-w-md w-full mx-4',
    'rounded-lg p-4 shadow-floating backdrop-blur-sm',
    'transition-all duration-300 ease-in-out',
    positionClasses[props.position],
    theme[props.variant].container,
    isVisible.value ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
  ]

  return baseClasses.join(' ')
})

const iconClasses = computed(() => {
  return [
    'flex-shrink-0 w-5 h-5',
    theme[props.variant].icon
  ]
})

// SVG icons for different variants
const getIconSvg = computed(() => {
  const icons = {
    [VARIANTS.ERROR]: `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
      </svg>
    `,
    [VARIANTS.WARNING]: `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
      </svg>
    `,
    [VARIANTS.INFO]: `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clip-rule="evenodd" />
      </svg>
    `,
    [VARIANTS.SUCCESS]: `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L6.53 10.47a.75.75 0 00-1.06 1.06l2.5 2.5a.75.75 0 001.154-.114l4-5.5z" clip-rule="evenodd" />
      </svg>
    `
  }
  return icons[props.variant]
})

// Auto-dismiss functionality
const startAutoDismiss = (): void => {
  if (props.autoDismiss && props.dismissTimeout > 0) {
    dismissTimer.value = setTimeout(() => {
      dismiss()
    }, props.dismissTimeout)
  }
}

const clearAutoDismiss = (): void => {
  if (dismissTimer.value !== null) {
    clearTimeout(dismissTimer.value)
    dismissTimer.value = null
  }
}

// Manual dismiss
const dismiss = (): void => {
  isVisible.value = false
  // Wait for animation to complete before emitting
  setTimeout(() => {
    emit('dismiss')
  }, 300)
}

// Handle close button click
const handleCloseClick = (): void => {
  clearAutoDismiss()
  dismiss()
}

// Lifecycle
onMounted(() => {
  startAutoDismiss()
})

onUnmounted(() => {
  clearAutoDismiss()
})
</script>

<template>
  <div 
    v-if="isVisible"
    :class="containerClasses"
    role="alert"
    :aria-live="variant === VARIANTS.ERROR ? 'assertive' : 'polite'"
  >
    <div class="flex items-start gap-3">
      <!-- Icon -->
      <span :class="iconClasses" aria-hidden="true" v-html="getIconSvg">
      </span>
      
      <!-- Message -->
      <div class="flex-1 min-w-0">
        <p class="text-sm leading-5">
          {{ message }}
        </p>
      </div>
      
      <!-- Close button -->
      <BaseButton
        v-if="dismissible"
        @click="handleCloseClick"
        class="flex-shrink-0 ml-2 p-1 rounded-md hover:bg-surface-variant transition-colors duration-150"
        type="button"
        aria-label="Dismiss notification"
        variant="ghost"
        size="icon"
      >
        <span class="w-4 h-4 text-on-surface-variant" aria-hidden="true">×</span>
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
/* Additional styles if needed */
</style>
